import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { Share2, Download, RotateCcw, Eye, EyeOff, Sparkles, AlertTriangle } from "lucide-react";
import html2canvas from "html2canvas";

export default function Results() {
  const location = useLocation();
  const navigate = useNavigate();
  const shareCardRef = useRef(null);
  const [showNSFW, setShowNSFW] = useState(true);
  
  const result = location.state?.result;
  const score = result?.score || 0;

  const getScoreVibe = (s) => {
    if (s >= 90) return "get ready to join the jensen club";
    if (s >= 80) return "get ready to fundmaxx";
    if (s >= 61) return "actually cooking something?";
    if (s >= 41) return "50/50 - could go either way tbh";
    if (s >= 21) return "corporate life is calling";
    if (s >= 11) return "nah you're cooked fam";
    return "sit down lad";
  };

  const vibe = getScoreVibe(score);

  if (!result) {
    navigate("/");
    return null;
  }

  const { verdict, verdict_image, breakdown, explanation, parody_quotes, next_actions, generated_idea } = result;

  // Prepare radar chart data
  const radarData = [
    { category: "Financial", value: breakdown.financial, fullMark: 100 },
    { category: "Emotional", value: breakdown.emotional, fullMark: 100 },
    { category: "Edge", value: breakdown.edge_experience, fullMark: 100 },
    { category: "Idea", value: breakdown.idea_strength, fullMark: 100 },
    { category: "Network", value: breakdown.network, fullMark: 100 },
  ];

  const handleShare = async (platform) => {
    try {
      const shareCard = shareCardRef.current;
      if (!shareCard) return;

      // Small delay to ensure styles are applied
      await new Promise(r => setTimeout(r, 100));

      const canvas = await html2canvas(shareCard, {
        backgroundColor: "#050505",
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: 800,
        height: 418,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => {
          const el = clonedDoc.querySelector('[data-share-card="true"]');
          if (el) {
            el.style.position = 'fixed';
            el.style.left = '0';
            el.style.top = '0';
            el.style.visibility = 'visible';
            el.style.opacity = '1';
          }
        }
      });
      
      canvas.toBlob((blob) => {
        if (platform === "download") {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `should-you-startup-score-${score}.png`;
          a.click();
          URL.revokeObjectURL(url);
        } else if (platform === "twitter") {
          const text = encodeURIComponent(`I scored ${score}/100 on byeC! "${vibe}" 🚀\n\nFind out your startup readiness: ${window.location.origin}`);
          window.open(`https://x.com/intent/post?text=${text}`, "_blank");
        } else if (platform === "instagram") {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `should-you-startup-score-${score}.png`;
          a.click();
          URL.revokeObjectURL(url);
          alert("Image downloaded! Upload it to Instagram to share.");
        }
      });
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden flex flex-col justify-between">
      {/* Background Gradients & Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f0f12_1px,transparent_1px),linear-gradient(to_bottom,#0f0f12_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
      
      <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-[#FF6B3D]/10 rounded-full blur-[100px] opacity-60 pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-indigo-900/10 rounded-full blur-[100px] opacity-60 pointer-events-none"></div>

      {/* Top Navbar */}
      <header className="relative z-10 w-full max-w-7xl mx-auto px-6 py-6 border-b border-white/5 backdrop-blur-md bg-[#050505]/40 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate("/")}
            className="bg-[#FF6B3D] text-black font-['Plus_Jakarta_Sans'] font-black text-xl px-4 py-1.5 rounded-lg shadow-[0_0_15px_rgba(255,107,61,0.25)] hover:bg-[#FF5522] transition-colors"
            data-testid="home-link"
          >
            byeC
          </button>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/questionnaire")}
            data-testid="retake-button"
            className="text-gray-400 hover:text-white hover:bg-white/5 rounded-xl"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            retake test
          </Button>
        </div>
      </header>

      <div 
        ref={shareCardRef} 
        data-share-card="true"
        style={{ 
          position: 'fixed',
          top: 0,
          left: '-9999px',
          width: '800px',
          height: '418px',
          backgroundColor: '#050505',
          padding: '48px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          zIndex: -1000,
          pointerEvents: 'none',
          visibility: 'visible',
          opacity: 1,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: '#FF6B3D', 
              color: 'black', 
              fontWeight: '950', 
              fontSize: '24px', 
              padding: '8px 16px', 
              borderRadius: '8px',
              lineHeight: '1',
              marginBottom: '16px',
              fontFamily: 'Arial, sans-serif'
            }}>
              byeC
            </div>
            <h1 style={{ fontSize: '32px', fontWeight: '800', color: 'white', margin: 0, letterSpacing: '-0.025em' }}>Should You Startup?</h1>
            <p style={{ color: '#a1a1aa', fontSize: '16px', marginTop: '4px', fontWeight: '500' }}>Startup Readiness Assessment</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '72px', fontWeight: '700', color: '#FF6B3D' }}>
              {score}
            </div>
            <p style={{ fontSize: '12px', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '4px', fontWeight: '700' }}>Score / 100</p>
          </div>
        </div>
        <div style={{ marginTop: 'auto' }}>
          <p style={{ fontSize: '24px', color: 'white', fontWeight: '700', lineHeight: '1.2', textTransform: 'uppercase' }}>
            "{vibe}"
          </p>
          <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <p style={{ fontSize: '12px', color: '#52525b' }}>
              Evaluate your readiness at: {window.location.origin.replace(/^https?:\/\//, "")}
            </p>
          </div>
        </div>
      </div>

      {/* Main Results Layout */}
      <main className="relative z-10 flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* LEFT COLUMN: Score Gauge & Sharing */}
          <div className="lg:col-span-4 space-y-6">
            <div 
              className="rounded-3xl border border-white/5 p-8 bg-[#0a0a0c]/60 backdrop-blur-xl shadow-[0_12px_40px_rgba(0,0,0,0.5)] text-center space-y-6"
              data-testid="score-card"
            >
              <p className="text-xs uppercase tracking-widest text-[#FF6B3D] font-['JetBrains_Mono'] font-bold">
                readiness score
              </p>
              
              {/* Radial Progress Gauge */}
              <div className="relative inline-flex items-center justify-center">
                <svg className="w-48 h-48">
                  <circle
                    cx="96"
                    cy="96"
                    r="84"
                    stroke="#18181b"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="84"
                    stroke="url(#orange-indigo-grad)"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${(score / 100) * 527} 527`}
                    strokeLinecap="round"
                    transform="rotate(-90 96 96)"
                  />
                  <defs>
                    <linearGradient id="orange-indigo-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#FF6B3D;" />
                      <stop offset="100%" stopColor="#6366f1" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute flex flex-col items-center">
                  <span className="text-6xl font-['JetBrains_Mono'] font-black text-white">
                    {score}
                  </span>
                  <span className="text-xs text-gray-500 uppercase tracking-widest mt-1">/ 100</span>
                </div>
              </div>

              {/* Verdict Text */}
              <div className="space-y-4">
                <h2 className="text-xl font-['Plus_Jakarta_Sans'] font-bold text-white leading-snug">
                  "{verdict}"
                </h2>
                {verdict_image && (
                  <div className="relative rounded-xl overflow-hidden border border-white/5 bg-[#050505] max-w-sm mx-auto shadow-2xl">
                    <img 
                      src={verdict_image} 
                      alt="verdict gif" 
                      className="w-full h-auto object-cover opacity-100"
                      loading="eager"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Sharing Widget */}
            <div className="rounded-3xl border border-white/5 p-6 bg-[#0a0a0c]/60 backdrop-blur-xl shadow-[0_12px_40px_rgba(0,0,0,0.5)] space-y-3">
              <p className="text-xs font-['JetBrains_Mono'] text-gray-500 uppercase tracking-wider mb-2">Share your assessment</p>
              <Button
                className="w-full h-12 bg-white text-black hover:bg-gray-200 font-bold rounded-xl transition-all"
                onClick={() => handleShare("twitter")}
                data-testid="share-twitter-button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
                </svg>
                Share on X
              </Button>
              <Button
                className="w-full h-12 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white font-semibold rounded-xl transition-all"
                onClick={() => handleShare("instagram")}
                data-testid="share-instagram-button"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share on Instagram
              </Button>
              <Button
                variant="outline"
                className="w-full h-12 text-white border-white/10 hover:bg-white/5 font-semibold rounded-xl transition-all"
                onClick={() => handleShare("download")}
                data-testid="download-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Results Card
              </Button>
            </div>
          </div>

          {/* RIGHT COLUMN: Chart, AI commentary, Actions, Quotes */}
          <div className="lg:col-span-8 space-y-6">
            
            {/* Radar Breakdown Chart */}
            <div className="rounded-3xl border border-white/5 bg-[#0a0a0c]/60 backdrop-blur-xl p-8 shadow-[0_12px_40px_rgba(0,0,0,0.5)]" data-testid="radar-chart">
              <h3 className="font-['Plus_Jakarta_Sans'] font-bold text-lg mb-6 text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FF6B3D]" />
                Ready Dimensions
              </h3>
              <div className="flex items-center justify-center">
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#27272A" />
                    <PolarAngleAxis 
                      dataKey="category" 
                      tick={{ fill: '#8A8A93', fontSize: 12, fontWeight: 500 }}
                    />
                    <PolarRadiusAxis 
                      angle={90} 
                      domain={[0, 100]} 
                      tick={{ fill: '#3F3F46', fontSize: 10 }}
                    />
                    <Radar
                      name="Score"
                      dataKey="value"
                      stroke="#FF6B3D"
                      fill="#FF6B3D"
                      fillOpacity={0.15}
                      strokeWidth={2.5}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-5 gap-2 mt-6 border-t border-white/5 pt-6">
                {radarData.map((item) => (
                  <div key={item.category} className="text-center">
                    <div className="text-xl font-['JetBrains_Mono'] font-black text-[#FF6B3D]">
                      {item.value}
                    </div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1 font-medium">
                      {item.category}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Commentary */}
            <div 
              className="rounded-3xl border border-white/5 bg-[#0a0a0c]/60 backdrop-blur-xl p-8 shadow-[0_12px_40px_rgba(0,0,0,0.5)] space-y-4"
              data-testid="ai-commentary"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-['Plus_Jakarta_Sans'] font-bold text-lg text-white">
                  Honest Assessment
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNSFW(!showNSFW)}
                  className="text-xs text-gray-500 hover:text-white hover:bg-white/5 rounded-lg px-2.5 py-1"
                >
                  {showNSFW ? <EyeOff className="w-3.5 h-3.5 mr-1 text-[#FF6B3D]" /> : <Eye className="w-3.5 h-3.5 mr-1 text-[#FF6B3D]" />}
                  {showNSFW ? "Hide" : "Show"} Brutal Truth
                </Button>
              </div>
              <div className="p-5 rounded-2xl bg-white/[0.01] border border-white/5">
                <p className="text-base leading-relaxed text-gray-200 font-medium">
                  {explanation}
                </p>
              </div>

              {showNSFW && (
                <div className="p-6 rounded-2xl bg-red-950/10 border border-red-500/20 space-y-3 mt-4">
                  <div className="flex items-center gap-2 text-red-400 font-extrabold text-xs uppercase tracking-widest">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span>Brutal Real Talk</span>
                  </div>
                  <p className="text-sm leading-relaxed text-gray-300 font-['JetBrains_Mono']">
                    {score >= 90
                      ? "get ready to join the jensen club. but actually, you're probably just lucky. don't let the leather jacket ego get to you yet."
                      : score >= 80
                      ? "ok real talk - you're actually not that special. half of silicon valley has your exact profile. the only question is: will you actually execute or just tweet about hustle culture?"
                      : score >= 60
                      ? "let's be honest - you're mid. you're not ready, but you're not hopeless. you'll probably try, fail, cope on linkedin about 'learnings', then try again in 3 years."
                      : score >= 40
                      ? "bro you're cooked. your network is weak, your idea is trash, and you're broke. go get a job at a real startup first and stop larping as a founder."
                      : "respectfully, you have zero business starting anything. you can't even handle uncertainty and you want to build a company? go work a 9-5 and keep your delusions on X."}
                  </p>
                </div>
              )}
            </div>

            {/* Generated Idea */}
            {generated_idea && (
              <div 
                className="rounded-3xl border border-[#FF6B3D]/20 bg-[#FF6B3D]/5 backdrop-blur-xl p-8 shadow-[0_12px_40px_rgba(0,0,0,0.5)] space-y-4"
              >
                <h3 className="font-['Plus_Jakarta_Sans'] font-bold text-lg text-white flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#FF6B3D]" />
                  Custom Startup Idea For You
                </h3>
                <div className="p-5 rounded-2xl bg-white/[0.01] border border-[#FF6B3D]/10">
                  <p className="text-base leading-relaxed text-[#FF6B3D] font-['JetBrains_Mono'] font-bold">
                    {generated_idea}
                  </p>
                </div>
              </div>
            )}

            {/* Parody Quotes */}
            <div 
              className="rounded-3xl border border-white/5 bg-[#0a0a0c]/60 backdrop-blur-xl p-8 shadow-[0_12px_40px_rgba(0,0,0,0.5)] space-y-6"
              data-testid="parody-quotes"
            >
              <div className="flex items-center justify-between border-b border-white/5 pb-4">
                <h3 className="font-['Plus_Jakarta_Sans'] font-bold text-lg text-white">
                  What gurus are saying
                </h3>
              </div>
              
              <div className="space-y-4">
                {parody_quotes.map((quote, index) => {
                  let name = "";
                  let quoteText = quote;
                  
                  if (quote.includes(":")) {
                    const parts = quote.split(":");
                    name = parts[0].trim();
                    quoteText = parts.slice(1).join(":").trim();
                  }
                  
                  return (
                    <div 
                      key={index}
                      className="p-5 rounded-2xl bg-white/[0.01] border border-white/5 hover:border-white/10 transition-colors"
                    >
                      {name && (
                        <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-wider font-extrabold text-[#FF6B3D] mb-1.5">{name}</p>
                      )}
                      <p className="text-sm italic leading-relaxed text-gray-300">
                        {quoteText.startsWith('"') ? quoteText : `"${quoteText}"`}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Next Actions */}
            <div 
              className="rounded-3xl border border-white/5 bg-[#0a0a0c]/60 backdrop-blur-xl p-8 shadow-[0_12px_40px_rgba(0,0,0,0.5)] space-y-6"
              data-testid="next-actions"
            >
              <h3 className="font-['Plus_Jakarta_Sans'] font-bold text-lg text-white">
                Next Actions
              </h3>
              <div className="space-y-3">
                {next_actions.map((action, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.01] border border-white/5 hover:border-white/10 transition-colors"
                  >
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#FF6B3D]/10 text-[#FF6B3D] border border-[#FF6B3D]/30 flex items-center justify-center text-sm font-['JetBrains_Mono'] font-bold">
                      {index + 1}
                    </span>
                    <p className="text-sm leading-relaxed pt-0.5 text-gray-300 font-medium">{action}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* Bottom CTA Retake */}
        <div className="mt-12 text-center">
          <Button
            size="lg"
            variant="outline"
            onClick={() => navigate("/questionnaire")}
            className="h-14 px-8 text-white border-white/10 hover:bg-white/5 rounded-2xl transition-all"
            data-testid="retake-bottom-button"
          >
            <RotateCcw className="w-5 h-5 mr-2" />
            Take the test again
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 w-full border-t border-white/5 py-8 text-center text-xs text-gray-500 backdrop-blur-md bg-[#050505]/20">
        <p>
          Made by Ishan Sukul aka{" "}
          <a
            href="https://twitter.com/isawsukul"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#FF6B3D] hover:underline font-medium"
          >
            @isawsukul
          </a>
        </p>
      </footer>
    </div>
  );
}
